import React, { useEffect, useMemo } from 'react';
import {
    Users,
    Building2,
    ShieldCheck,
    Wrench,
    LayoutDashboard,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
    CommandDialog,
    CommandInput,
    CommandList,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandSeparator,
} from '../ui/command';
import type { QuadrantItemData } from '../dashboard/QuadrantItem';
import { useQuadrantItems } from '../../hooks/useQuadrantItems';
import { categoryColors } from '../../theme/palette';

interface QuadrantMeta {
    title: string;
    icon: LucideIcon;
    color: string;
    quadrant: number;
}

const QUADRANT_META: QuadrantMeta[] = [
    { title: 'People', icon: Users, color: categoryColors.people, quadrant: 0 },
    { title: 'Core & Places', icon: Building2, color: categoryColors.corePlaces, quadrant: 1 },
    { title: 'Administration', icon: ShieldCheck, color: categoryColors.admin, quadrant: 2 },
    { title: 'Tools & Meta', icon: Wrench, color: categoryColors.toolsMeta, quadrant: 3 },
];

interface CommandPaletteProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onNavigate: (
        destination: string,
        isEntity?: boolean,
        category?: 'person' | 'locality' | 'other' | 'meta' | 'utilities' | 'blacklist' | 'admin'
    ) => void;
    onNavigateHash?: (hash: string) => void;
    authResolved: boolean;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({
    open,
    onOpenChange,
    onNavigate,
    onNavigateHash,
    authResolved,
}) => {
    const { quadrantItems } = useQuadrantItems({ onNavigate, authResolved });

    const groups = useMemo(() => {
        const g: QuadrantItemData[][] = [[], [], [], []];
        quadrantItems.forEach(item => {
            const q = Math.min(item.quadrant, 3);
            g[q].push(item);
        });
        return g;
    }, [quadrantItems]);

    useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                onOpenChange(!open);
            }
        };
        document.addEventListener('keydown', down);
        return () => document.removeEventListener('keydown', down);
    }, [open, onOpenChange]);

    const handleSelect = (item: QuadrantItemData) => {
        item.onClick();
        onOpenChange(false);
    };

    return (
        <CommandDialog open={open} onOpenChange={onOpenChange}>
            <CommandInput placeholder="Search entities, tools, and actions..." />
            <CommandList>
                <CommandEmpty>No results found.</CommandEmpty>

                {/* Dashboard */}
                <CommandGroup heading="Navigation">
                    <CommandItem
                        onSelect={() => {
                            onNavigateHash?.('dashboard');
                            onOpenChange(false);
                        }}
                    >
                        <LayoutDashboard size={16} className="mr-2 text-muted-foreground" />
                        <span>Dashboard</span>
                    </CommandItem>
                </CommandGroup>

                {/* Quadrant groups */}
                {QUADRANT_META.map((qm) => {
                    const items = groups[qm.quadrant];
                    if (!items.length) return null;
                    const QIcon = qm.icon;
                    return (
                        <React.Fragment key={qm.quadrant}>
                            <CommandSeparator />
                            <CommandGroup heading={qm.title}>
                                {items.map((item) => {
                                    const ItemIcon = item.icon;
                                    return (
                                        <CommandItem
                                            key={item.id}
                                            onSelect={() => handleSelect(item)}
                                        >
                                            <ItemIcon size={16} className="mr-2" style={{ color: item.color }} />
                                            <span>{item.label}</span>
                                            {item.count != null && (
                                                <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                                                    {item.count.toLocaleString()}
                                                </span>
                                            )}
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                        </React.Fragment>
                    );
                })}
            </CommandList>
        </CommandDialog>
    );
};
